import type { CharacterInfo, ModelInfo, RateLimitStatus } from '@gofranz/gpt-muse-common';
import { ModelCapability } from '@gofranz/llm-core-common';
import {
  ActionIcon,
  Badge,
  Box,
  Center,
  CloseButton,
  Group,
  Text,
  Textarea,
  Tooltip,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconDownload, IconMicrophone, IconPhoto, IconPlayerStop, IconSend, IconSettings } from '@tabler/icons-react';
import { useRef, useState } from 'react';
import type { ChatMessage, ChatMessageAttachment } from '../../lib/chat-db';
import { formatTimeUntilReset } from '../../lib/chat-utils';
import { CharacterSelector } from './CharacterSelector';
import { ModelSelector } from './ModelSelector';
import { SaveChatModal } from './SaveChatModal';
import { SettingsModal, type ChatSettings, type ReasoningSettings, type WebSearchSettings } from './SettingsModal';

interface ChatInputBarProps {
  selectedModel: string;
  isAuthenticated: boolean;
  isLoading?: boolean;
  isStreaming?: boolean;
  rateLimitInfo?: RateLimitStatus | null;
  selectedCharacter?: string | null;
  models?: ModelInfo[];
  isLoadingModels?: boolean;
  messages: ChatMessage[];
  currentReasoningSettings?: ReasoningSettings;
  currentChatSettings?: ChatSettings;
  currentWebSearchSettings?: WebSearchSettings;
  inputPlaceholder?: string;
  characters?: CharacterInfo[];
  onSubmit: (message: string, attachment?: ChatMessageAttachment) => void;
  onStopStreaming?: () => void;
  onModelSelect?: (modelId: string) => void;
  onCharacterSelect?: (characterId: string | null) => void;
  onReasoningSettingsChange?: (modelId: string, settings: ReasoningSettings) => void;
  onChatSettingsChange?: (settings: ChatSettings) => void;
  onWebSearchSettingsChange?: (modelId: string, settings: WebSearchSettings) => void;
}

export function ChatInputBar({
  selectedModel,
  isAuthenticated,
  isLoading = false,
  isStreaming = false,
  rateLimitInfo,
  selectedCharacter,
  models = [],
  isLoadingModels = false,
  messages,
  currentReasoningSettings,
  currentChatSettings,
  currentWebSearchSettings,
  inputPlaceholder,
  characters = [],
  onSubmit,
  onStopStreaming,
  onModelSelect,
  onCharacterSelect,
  onReasoningSettingsChange,
  onChatSettingsChange,
  onWebSearchSettingsChange,
}: ChatInputBarProps) {
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [saveChatModalOpen, setSaveChatModalOpen] = useState(false);
  const [attachment, setAttachment] = useState<ChatMessageAttachment | null>(null);

  const imageInputRef = useRef<HTMLInputElement>(null);
  const audioInputRef = useRef<HTMLInputElement>(null);

  // Check if user has reached rate limit (free users only)
  const isRateLimited = !isAuthenticated && rateLimitInfo?.remaining_requests === 0;

  // Check if selected model supports vision (for image/audio input)
  const selectedModelInfo = models.find(m => m.id === selectedModel);
  const supportsVision = selectedModelInfo?.capabilities?.includes(ModelCapability.Vision) ?? false;

  const form = useForm({
    initialValues: {
      message: '',
    },
    validate: {
      message: (value) => (!value.trim() ? 'Message cannot be empty' : null),
    },
  });

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>, type: 'image' | 'audio') => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (5MB for images, 10MB for audio)
    const maxSize = type === 'image' ? 5 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      alert(`File too large. Maximum size: ${type === 'image' ? '5MB' : '10MB'}`);
      return;
    }

    // Read file as base64
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Extract base64 data (remove data URL prefix)
      const base64Data = result.split(',')[1];

      setAttachment({
        data: base64Data,
        mimeType: file.type,
        type,
      });
    };
    reader.onerror = () => {
      alert('Failed to read file');
    };
    reader.readAsDataURL(file);

    // Reset the input so the same file can be selected again
    event.target.value = '';
  };

  const handleRemoveAttachment = () => {
    setAttachment(null);
  };

  const handleSubmit = (values: { message: string }) => {
    if (!selectedModel || !values.message.trim() || isRateLimited) return;

    onSubmit(values.message.trim(), attachment || undefined);
    form.reset();
    setAttachment(null); // Clear attachment after sending
  };

  return (
    <>
      <Center>
        <Box
          w="100%"
          style={{
            maxWidth: '900px',
            minWidth: 0,
            width: '100%',
          }}
        >
          <form onSubmit={form.onSubmit(handleSubmit)}>
            {/* Hidden file inputs */}
            <input
              ref={imageInputRef}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              style={{ display: 'none' }}
              onChange={(e) => handleFileSelect(e, 'image')}
            />
            <input
              ref={audioInputRef}
              type="file"
              accept="audio/wav,audio/mp3,audio/mpeg"
              style={{ display: 'none' }}
              onChange={(e) => handleFileSelect(e, 'audio')}
            />

            {/* Attachment preview */}
            {attachment && (
              <Group gap="xs" mb="xs">
                <Badge
                  size="lg"
                  variant="light"
                  color={attachment.type === 'image' ? 'blue' : 'grape'}
                  rightSection={
                    <CloseButton
                      size="xs"
                      onClick={handleRemoveAttachment}
                      aria-label="Remove attachment"
                    />
                  }
                >
                  {attachment.type === 'image' ? '🖼️ Image' : '🎵 Audio'} attached
                </Badge>
              </Group>
            )}

            <Group align="flex-end" gap="sm">
              {/* File upload buttons - only show for vision-capable models */}
              <Tooltip label={supportsVision ? "Attach image (PNG, JPEG, GIF, WebP)" : "Selected model doesn't support image input"}>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  onClick={() => imageInputRef.current?.click()}
                  disabled={isStreaming || isLoading || isRateLimited || !!attachment || !supportsVision}
                  size="lg"
                  style={{ opacity: supportsVision ? 1 : 0.3 }}
                >
                  <IconPhoto size={18} />
                </ActionIcon>
              </Tooltip>

              <Tooltip label={supportsVision ? "Attach audio (WAV, MP3)" : "Selected model doesn't support audio input"}>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  onClick={() => audioInputRef.current?.click()}
                  disabled={isStreaming || isLoading || isRateLimited || !!attachment || !supportsVision}
                  size="lg"
                  style={{ opacity: supportsVision ? 1 : 0.3 }}
                >
                  <IconMicrophone size={18} />
                </ActionIcon>
              </Tooltip>

              <Textarea
                {...form.getInputProps('message')}
                placeholder={
                  isRateLimited
                    ? "Rate limit reached. Please wait for reset or sign up for unlimited access"
                    : inputPlaceholder || "Type your message here..."
                }
                autosize
                minRows={1}
                maxRows={6}
                flex={1}
                disabled={isStreaming || isLoading || isRateLimited}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    const result = form.validate();
                    if (!result.hasErrors) {
                      handleSubmit(form.values);
                    }
                  }
                }}
              />
              
              <Tooltip
                label={
                  isStreaming
                    ? 'Stop generation'
                    : isRateLimited
                      ? 'Rate limit reached - please wait or sign up'
                      : 'Send message (Enter)'
                }
              >
                {isStreaming ? (
                  <ActionIcon
                    size="lg"
                    variant="filled"
                    color="red"
                    onClick={onStopStreaming}
                  >
                    <IconPlayerStop size={18} />
                  </ActionIcon>
                ) : (
                  <ActionIcon
                    type="submit"
                    size="lg"
                    variant="filled"
                    color="blue"
                    disabled={isLoading || isRateLimited || !form.values.message.trim()}
                  >
                    <IconSend size={18} />
                  </ActionIcon>
                )}
              </Tooltip>
            </Group>
          </form>
          
          {/* Rate limit warning message */}
          {isRateLimited && rateLimitInfo && (
            <Text size="xs" c="orange" mt="xs" ta="center" fw={500}>
              ⚠️ Rate limit reached ({rateLimitInfo.remaining_requests}/{rateLimitInfo.limit_requests} requests used)
              <br />
              Resets in {formatTimeUntilReset(rateLimitInfo.reset_time)} • Sign up for unlimited access
            </Text>
          )}
          
          <Group justify="space-between" align="center" mt="xs">
            <Group gap="xs" align="center">
              <ActionIcon
                variant="subtle"
                color="gray"
                onClick={() => setSaveChatModalOpen(true)}
                disabled={messages.length === 0}
                aria-label="Save chat"
                size="sm"
              >
                <IconDownload size={16} />
              </ActionIcon>

              <ActionIcon
                variant="subtle"
                color="gray"
                onClick={() => setSettingsModalOpen(true)}
                disabled={!selectedModel}
                aria-label="Settings"
                size="sm"
              >
                <IconSettings size={16} />
              </ActionIcon>

              <CharacterSelector
                selectedCharacter={selectedCharacter ?? null}
                onCharacterSelect={onCharacterSelect || (() => { })}
                onModelSelect={onModelSelect || (() => { })}
                models={models}
                characters={characters}
                disabled={messages.length > 0}
                isLoading={false}
                isDesktop={true}
              />
            </Group>

            <Text size="xs" c="dimmed" ta="center">
              {isAuthenticated ? 'Authenticated chat' : 'Public chat'}
            </Text>

            <ModelSelector
              models={models}
              selectedModel={selectedModel}
              onModelSelect={onModelSelect || (() => { })}
              isLoading={isLoadingModels}
              isLoggedIn={isAuthenticated}
              isDesktop={true}
            />
          </Group>
        </Box>
      </Center>

      <SaveChatModal
        opened={saveChatModalOpen}
        onClose={() => setSaveChatModalOpen(false)}
        messages={messages}
        selectedCharacter={selectedCharacter}
        characters={characters}
      />

      <SettingsModal
        opened={settingsModalOpen}
        onClose={() => setSettingsModalOpen(false)}
        selectedModel={models.find(m => m.id === selectedModel)}
        onSettingsChange={onReasoningSettingsChange || (() => { })}
        initialSettings={currentReasoningSettings}
        onChatSettingsChange={onChatSettingsChange || (() => { })}
        initialChatSettings={currentChatSettings}
        onWebSearchSettingsChange={onWebSearchSettingsChange || (() => { })}
        initialWebSearchSettings={currentWebSearchSettings}
      />
    </>
  );
}